import type { ActivityContext, ActivityHandle } from '../../core/activity';
import { makeDraggable } from '../../shared/drag-drop';
import levelsData from './levels.json';
import { generatePuzzle, computeResult, type Puzzle, type Cage, type Operator } from './generator';

interface SubLevel {
  random: boolean;
  length: number;
  size: number;
  operators: string[];
}

interface Level {
  level: number;
  objective: string;
  difficulty: number;
  data: SubLevel[];
}

interface LevelsFile {
  levels: Level[];
}

const LEVELS = (levelsData as LevelsFile).levels;
const SUBLEVELS_PER_LEVEL = 5;

const OBJECTIVE_PT: Record<string, string> = {
  'Small grids with \\u002B operator.': 'Grades pequenas só com +',
  'Small grids with \\u002B and \\u2212 operators.': 'Grades pequenas com + e −',
  'Small grids with \\u002B, \\u2212 and \\u00D7 operators.': 'Grades pequenas com +, − e ×',
  'Medium grids with \\u002B, \\u2212 and \\u00D7 operators.': 'Grades médias com +, − e ×',
  'Large grids with \\u002B, \\u2212, \\u00D7 and \\u2215 operators.': 'Grades grandes com +, −, × e ÷',
};

export function mountCalcudoku(ctx: ActivityContext): ActivityHandle {
  const level = LEVELS.find((l) => l.level === ctx.level) ?? LEVELS[0];
  if (!level) {
    ctx.root.innerHTML = '<p>Nível não encontrado.</p>';
    return { unmount: () => { ctx.root.innerHTML = ''; } };
  }

  const cfg = level.data[0]!;
  const size = cfg.size;
  const totalSubs = SUBLEVELS_PER_LEVEL;
  const objectivePt = OBJECTIVE_PT[level.objective] ?? level.objective;

  let subIndex = 0;
  let correctCount = 0;
  let puzzle: Puzzle = generatePuzzle(size, cfg.operators);
  let cellToCage: number[] = [];
  let values: (number | 0)[] = new Array(size * size).fill(0);
  let selectedKey: number | 'erase' | null = null;
  let errorTimer: number | null = null;
  let buttonsBlocked = false;
  let cageErrors = new Set<number>();

  const page = document.createElement('div');
  page.className = 'activity-page';
  page.innerHTML = `
    <header class="activity-bar">
      <button class="activity-bar__back" type="button" aria-label="Voltar ao hub">← Voltar</button>
      <div class="activity-bar__title">Calcudoku</div>
      <div class="activity-bar__level">Nível ${level.level} de ${LEVELS.length}</div>
    </header>
    <section class="activity-stage calcu">
      <div class="feedback" aria-live="polite"></div>
      <p class="calcu__progress" aria-live="polite"></p>
      <p class="calcu__hint">${objectivePt}</p>
      <div class="calcu__grid-wrap"></div>
      <div class="calcu__keypad"></div>
      <div class="calcu__actions">
        <button class="btn btn--secondary calcu__reset" type="button">↺ Limpar</button>
      </div>
    </section>
  `;
  ctx.root.appendChild(page);

  const progressEl = page.querySelector<HTMLElement>('.calcu__progress')!;
  const gridWrap = page.querySelector<HTMLElement>('.calcu__grid-wrap')!;
  const keypadEl = page.querySelector<HTMLElement>('.calcu__keypad')!;
  const feedback = page.querySelector<HTMLElement>('.feedback')!;
  const resetBtn = page.querySelector<HTMLButtonElement>('.calcu__reset')!;
  page.querySelector<HTMLElement>('.activity-bar__back')!.addEventListener('click', () => ctx.onExit());
  resetBtn.addEventListener('click', () => {
    if (buttonsBlocked) return;
    values = new Array(size * size).fill(0);
    cageErrors.clear();
    ctx.audio.play('click');
    renderGrid();
  });

  function initRound(): void {
    if (errorTimer !== null) {
      window.clearTimeout(errorTimer);
      errorTimer = null;
    }
    puzzle = generatePuzzle(size, cfg.operators);
    cellToCage = new Array(size * size).fill(0);
    puzzle.cages.forEach((cage, cageIdx) => {
      for (const i of cage.indexes) cellToCage[i] = cageIdx;
    });
    values = new Array(size * size).fill(0);
    selectedKey = null;
    buttonsBlocked = false;
    cageErrors.clear();
    renderProgress();
    renderGrid();
    renderKeypad();
  }

  function renderProgress(): void {
    progressEl.textContent = `Pergunta ${subIndex + 1} de ${totalSubs}`;
  }

  function cellNeighbor(id: number, dir: 'top' | 'right' | 'bottom' | 'left'): number {
    const row = Math.floor(id / size);
    const col = id % size;
    if (dir === 'top') return row > 0 ? id - size : -1;
    if (dir === 'bottom') return row < size - 1 ? id + size : -1;
    if (dir === 'left') return col > 0 ? id - 1 : -1;
    return col < size - 1 ? id + 1 : -1;
  }

  function isCageBoundary(id: number, dir: 'top' | 'right' | 'bottom' | 'left'): boolean {
    const n = cellNeighbor(id, dir);
    if (n === -1) return true;
    return cellToCage[n] !== cellToCage[id];
  }

  function renderGrid(): void {
    const cell = 64;
    const dim = size * cell;
    gridWrap.innerHTML = '';
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', `0 0 ${dim} ${dim}`);
    svg.setAttribute('class', 'calcu__grid');

    // Cell backgrounds (clickable)
    for (let id = 0; id < size * size; id++) {
      const row = Math.floor(id / size);
      const col = id % size;
      const x = col * cell;
      const y = row * cell;
      const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      rect.setAttribute('x', String(x));
      rect.setAttribute('y', String(y));
      rect.setAttribute('width', String(cell));
      rect.setAttribute('height', String(cell));
      const cageIdx = cellToCage[id]!;
      const inError = cageErrors.has(cageIdx);
      const cls = inError ? 'calcu__cell-bg calcu__cell-bg--cage-error' : 'calcu__cell-bg';
      rect.setAttribute('class', cls);
      rect.setAttribute('data-id', String(id));
      rect.addEventListener('click', () => onCellClick(id));
      svg.appendChild(rect);
    }

    // Cage borders (heavy)
    for (let id = 0; id < size * size; id++) {
      const row = Math.floor(id / size);
      const col = id % size;
      const x = col * cell;
      const y = row * cell;
      const cageIdx = cellToCage[id]!;
      const inError = cageErrors.has(cageIdx);
      const borderCls = inError ? 'calcu__cage-border calcu__cage-border--error' : 'calcu__cage-border';
      const sides: { dir: 'top' | 'right' | 'bottom' | 'left'; x1: number; y1: number; x2: number; y2: number }[] = [
        { dir: 'top', x1: x, y1: y, x2: x + cell, y2: y },
        { dir: 'right', x1: x + cell, y1: y, x2: x + cell, y2: y + cell },
        { dir: 'bottom', x1: x, y1: y + cell, x2: x + cell, y2: y + cell },
        { dir: 'left', x1: x, y1: y, x2: x, y2: y + cell },
      ];
      for (const s of sides) {
        if (isCageBoundary(id, s.dir)) {
          const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
          line.setAttribute('x1', String(s.x1));
          line.setAttribute('y1', String(s.y1));
          line.setAttribute('x2', String(s.x2));
          line.setAttribute('y2', String(s.y2));
          line.setAttribute('class', borderCls);
          svg.appendChild(line);
        }
      }
    }

    // Cage hints (operator + result) on top-left cell of each cage
    puzzle.cages.forEach((cage) => {
      const headIdx = Math.min(...cage.indexes);
      const row = Math.floor(headIdx / size);
      const col = headIdx % size;
      const x = col * cell + 4;
      const y = row * cell + 12;
      const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      text.setAttribute('x', String(x));
      text.setAttribute('y', String(y));
      text.setAttribute('class', 'calcu__cell-hint');
      text.textContent = cage.indexes.length === 1 ? '' : `${cage.result}${cage.operator}`;
      svg.appendChild(text);
      if (cage.indexes.length === 1) {
        const txt2 = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        txt2.setAttribute('x', String(x));
        txt2.setAttribute('y', String(y));
        txt2.setAttribute('class', 'calcu__cell-hint');
        txt2.textContent = `${cage.result}`;
        svg.appendChild(txt2);
      }
    });

    // Values
    for (let id = 0; id < size * size; id++) {
      if (values[id] === 0) continue;
      const row = Math.floor(id / size);
      const col = id % size;
      const cx = col * cell + cell / 2;
      const cy = row * cell + cell / 2 + 4;
      const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      text.setAttribute('x', String(cx));
      text.setAttribute('y', String(cy));
      text.setAttribute('class', 'calcu__cell-value');
      text.setAttribute('font-size', String(Math.floor(cell * 0.55)));
      text.textContent = String(values[id]);
      svg.appendChild(text);
    }

    gridWrap.appendChild(svg);
  }

  function renderKeypad(): void {
    keypadEl.innerHTML = '';
    for (let n = 1; n <= size; n++) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'calcu__key';
      if (selectedKey === n) btn.classList.add('calcu__key--selected');
      btn.textContent = String(n);
      btn.disabled = buttonsBlocked;
      btn.addEventListener('click', () => onKeyClick(n));
      keypadEl.appendChild(btn);
      const num = n;
      makeDraggable(btn, {
        getDropTarget: (x, y) => {
          const e = document.elementFromPoint(x, y);
          return e?.closest<HTMLElement>('rect[data-id]') as HTMLElement | null;
        },
        onDrop: (targetEl) => {
          const cellId = parseInt(targetEl.dataset['id'] ?? '-1', 10);
          if (cellId < 0) return;
          selectedKey = num;
          onCellClick(cellId);
        },
      });
    }
    const eraseBtn = document.createElement('button');
    eraseBtn.type = 'button';
    eraseBtn.className = 'calcu__key calcu__key--erase';
    if (selectedKey === 'erase') eraseBtn.classList.add('calcu__key--selected');
    eraseBtn.textContent = '⌫';
    eraseBtn.setAttribute('aria-label', 'Apagar');
    eraseBtn.disabled = buttonsBlocked;
    eraseBtn.addEventListener('click', () => onKeyClick('erase'));
    keypadEl.appendChild(eraseBtn);
    makeDraggable(eraseBtn, {
      getDropTarget: (x, y) => {
        const e = document.elementFromPoint(x, y);
        return e?.closest<HTMLElement>('rect[data-id]') as HTMLElement | null;
      },
      onDrop: (targetEl) => {
        const cellId = parseInt(targetEl.dataset['id'] ?? '-1', 10);
        if (cellId < 0) return;
        selectedKey = 'erase';
        onCellClick(cellId);
      },
    });
  }

  function onKeyClick(key: number | 'erase'): void {
    if (buttonsBlocked) return;
    selectedKey = selectedKey === key ? null : key;
    ctx.audio.play('click');
    renderKeypad();
  }

  function onCellClick(id: number): void {
    if (buttonsBlocked) return;
    if (selectedKey === null) return;
    if (selectedKey === 'erase') {
      if (values[id] !== 0) {
        values[id] = 0;
        cageErrors.clear();
        ctx.audio.play('click');
        renderGrid();
      }
      return;
    }
    if (values[id] !== 0) {
      values[id] = 0;
      cageErrors.clear();
      ctx.audio.play('click');
      renderGrid();
      return;
    }
    const conflict = findConflict(id, selectedKey);
    if (conflict !== -1) {
      ctx.audio.play('wrong');
      flashCell(conflict);
      flashCell(id);
      return;
    }
    values[id] = selectedKey;
    cageErrors.clear();
    ctx.audio.play('click');
    renderGrid();
    if (isFull()) checkSolution();
  }

  function findConflict(id: number, value: number): number {
    const row = Math.floor(id / size);
    const col = id % size;
    for (let c = 0; c < size; c++) {
      const idx = row * size + c;
      if (idx !== id && values[idx] === value) return idx;
    }
    for (let r = 0; r < size; r++) {
      const idx = r * size + col;
      if (idx !== id && values[idx] === value) return idx;
    }
    return -1;
  }

  function flashCell(id: number): void {
    const el = gridWrap.querySelector<SVGElement>(`rect[data-id="${id}"]`);
    if (!el) return;
    el.classList.add('calcu__cell-bg--error');
    window.setTimeout(() => el.classList.remove('calcu__cell-bg--error'), 700);
  }

  function isFull(): boolean {
    for (let i = 0; i < size * size; i++) {
      if (values[i] === 0) return false;
    }
    return true;
  }

  function checkSolution(): void {
    cageErrors.clear();
    let allOk = true;
    puzzle.cages.forEach((cage: Cage, cageIdx) => {
      const result = computeResult(values as number[], cage.indexes, cage.operator as Operator);
      if (result !== cage.result) {
        cageErrors.add(cageIdx);
        allOk = false;
      }
    });
    if (allOk) {
      buttonsBlocked = true;
      ctx.audio.play('correct');
      showFeedback('ok');
      correctCount++;
      subIndex++;
      window.setTimeout(() => {
        if (subIndex < totalSubs) initRound();
        else finishLevel();
      }, 900);
    } else {
      ctx.audio.play('wrong');
      showFeedback('err');
      renderGrid();
    }
  }

  function showFeedback(kind: 'ok' | 'err'): void {
    feedback.textContent = kind === 'ok' ? '✓' : '✗';
    feedback.className = `feedback feedback--show feedback--${kind}`;
    window.setTimeout(() => feedback.classList.remove('feedback--show'), 500);
  }

  function finishLevel(): void {
    ctx.audio.play('win');
    ctx.onComplete(level.level, correctCount);
    const stage = page.querySelector<HTMLElement>('.activity-stage')!;
    stage.innerHTML = `
      <h2 class="activity-finish__title">Nível ${level.level} concluído!</h2>
      <p class="activity-finish__text">Você resolveu ${correctCount} de ${totalSubs} calcudokus.</p>
      <div class="activity-finish__actions">
        <button class="btn btn--secondary" type="button" data-act="hub">Voltar ao hub</button>
        ${level.level < LEVELS.length
          ? `<button class="btn btn--primary" type="button" data-act="next">Próximo nível</button>`
          : ''}
      </div>
    `;
    stage.querySelector<HTMLElement>('[data-act="hub"]')!.addEventListener('click', () => ctx.onExit());
    const next = stage.querySelector<HTMLElement>('[data-act="next"]');
    if (next) {
      next.addEventListener('click', () => {
        window.location.hash = `/calcudoku/${level.level + 1}`;
      });
    }
  }

  initRound();

  return {
    unmount(): void {
      if (errorTimer !== null) {
        window.clearTimeout(errorTimer);
        errorTimer = null;
      }
      ctx.root.innerHTML = '';
    },
  };
}
